import { Observable, map, of } from 'rxjs';
import { HttpClientInterface } from '@itk/http-client-interface';
import {
  CheatSheet,
  CheatSheetRaw,
  CheatSheetRequest,
  Companies,
  CompaniesRequest,
  DvhCompareQuoteRequest,
  DvhFullQuoteRequest,
  DvhQuoteResponse,
  ExcludedQuote,
  FexCompareQuoteRequest,
  FexFullQuoteRequest,
  FexLiteQuoteRequest,
  HiFullQuoteRequest,
  ListCompanies,
  MedSuppCompareQuoteRequest,
  MedSuppFullQuoteRequest,
  MedSuppLiteQuoteRequest,
  Quote,
  QuoterResults,
  TermCompareQuoteRequest,
  TermFullQuoteRequest,
  TermLiteQuoteRequest,
} from '../django-api';
import { DVH_TOOLKIT_CODE, ToolkitCode } from '@itk/types';

export class QuoterEndpoint {
  constructor(private readonly http: HttpClientInterface) {}

  listCompanies(req: ToolkitCode): Observable<ListCompanies> {
    switch (req) {
      case DVH_TOOLKIT_CODE:
        return this.http
          .get<{ name: string; title: string }[]>({
            url: '/ancillary/dvh/carriers/',
            queryParams: { toolkit: DVH_TOOLKIT_CODE },
          })
          .pipe(map((x) => x.map((y) => ({ name: y.title, id: y.name }))));
      default:
        return of([]);
    }
  }

  companies(req: CompaniesRequest): Observable<Companies> {
    return this.http.get<Companies>({
      url: '/quoter/compare/',
      queryParams: req as unknown as Record<string, string>,
    });
  }

  liteQuote(request: LiteQuoteRequest): Observable<QuoterResults> {
    return this.http.post<QuoterResults, LiteQuoteRequest>({
      url: `/quoter/lite/`,
      body: request,
    });
  }

  getCompanyQuote(
    company: string,
    request: FullQuoteRequest,
  ): Observable<QuoterResults> {
    let url;
    switch (request.toolkit) {
      case DVH_TOOLKIT_CODE:
        url = `/ancillary/dvh/quote/${company}/`;
        break;
      default:
        url = '/quoter/';
    }

    return this.http
      .post<QuoterResults | DvhQuoteResponse[], FullQuoteRequest>({
        url,
        body: request,
      })
      .pipe(
        map((x) => {
          if (request.toolkit === DVH_TOOLKIT_CODE) {
            const dvhResult = x as DvhQuoteResponse[];
            return {
              quotes: dvhResult.map((y) => {
                return {
                  dvhResponse: { ...y.ruleResult, product_name: y.productName },
                } as Quote;
              }),
              excluded: [],
            };
          }

          return x as QuoterResults;
        }),
      );
  }

  getQuote(request: FullQuoteRequest): Observable<QuoterResults> {
    let url = '/quoter/';

    if (request.toolkit === DVH_TOOLKIT_CODE) {
      url = '/ancillary/dvh/quote/';
    }

    return this.http
      .post<QuoterResults | DvhQuoteResponse[], FullQuoteRequest>({
        url,
        body: request,
      })
      .pipe(
        map((x) => {
          if (request.toolkit === DVH_TOOLKIT_CODE) {
            const dvhResult = x as DvhQuoteResponse[];
            return {
              quotes: dvhResult.map((y) => {
                return {
                  dvhResponse: { ...y.ruleResult, product_name: y.productName },
                };
              }),
              excluded: [],
            };
          }

          return x as QuoterResults;
        }),
      );
  }

  compareQuote(req: CompareQuoteRequest): Observable<QuoterResults> {
    let url = `/quoter/compare/`;

    if (req.toolkit === DVH_TOOLKIT_CODE) {
      url = '/ancillary/dvh/quote/';
    }

    return this.http
      .post<{ data: Quote[] } | DvhQuoteResponse[], CompareQuoteRequest>({
        url,
        body: req,
      })
      .pipe(
        map((x) => {
          let quotes: Quote[];
          if ('data' in x) {
            quotes = x.data;
          } else {
            const data = x;
            quotes = data.map((y) => ({
              dvhResponse: { ...y.ruleResult, product_name: y.productName },
            }));
          }
          return {
            quotes: quotes.filter((y) => !!y),
            excluded: [] as ExcludedQuote[],
          };
        }),
      );
  }

  cheatsheetQuote(req: CheatSheetRequest): Observable<CheatSheet> {
    return this.http
      .post<CheatSheetRaw, CheatSheetRequest>({
        url: '/cheatsheet/',
        body: req,
      })
      .pipe(
        map((x) =>
          Object.entries(x).reduce(
            (_, [condition, items]) => ({ condition, items }),
            {} as CheatSheet,
          ),
        ),
      );
  }
}

/* Add new quoter requests here */
type FullQuoteRequest =
  | FexFullQuoteRequest
  | TermFullQuoteRequest
  | MedSuppFullQuoteRequest
  | HiFullQuoteRequest
  | DvhFullQuoteRequest;

type CompareQuoteRequest =
  | FexCompareQuoteRequest
  | MedSuppCompareQuoteRequest
  | TermCompareQuoteRequest
  | DvhCompareQuoteRequest;

type LiteQuoteRequest =
  | FexLiteQuoteRequest
  | TermLiteQuoteRequest
  | MedSuppLiteQuoteRequest;
